import { useCallback, useEffect, useMemo, useState } from 'react'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { UpgradeDialog } from '@/components/panels/UpgradeDialog'
import { useAuth } from '@/hooks/useAuth'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useEffectiveMidi } from '@/hooks/useEffectiveMidi'
import { usePlatform } from '@/hooks/usePlatform'
import { useAudioEngine, KITS } from '@/hooks/useAudioEngine'
import { useWebMidi } from '@/hooks/useWebMidi'
import { useSequencerTriggers } from '@/hooks/useSequencerTriggers'
import {
  useSequencerStore,
  selectCurrentProject,
  selectCurrentContainer,
} from '@/stores/sequencerStore'
import { useMidiStore } from '@/stores/midiStore'
import { PadGrid } from './PadGrid'
import { StepGrid } from './StepGrid'
import { Transport } from './Transport'
import { KitPicker } from './KitPicker'
import { VirtualInputBadge } from './VirtualInputBadge'
import { FxBank } from './FxBank'
import { StepInspectorPanel } from './StepInspectorPanel'
import { ProjectBar } from './ProjectBar'
import { BankSwitcher } from './BankSwitcher'
import { SongLayout } from './song/SongLayout'
import { ScramblerPanel } from './ScramblerRandomizer'
import { Inspector, InspectorToggle } from '@/components/werkshop/Inspector'
import { ProjectSaveRename } from './ProjectSaveRename'
import { useInspectorStore } from '@/stores/inspectorStore'
import { useUIStore } from '@/stores/uiStore'
import { SequencerTabs } from '@/components/werkshop/SequencerTabs'
import { collectRequiredSampleIds } from '@/lib/collectRequiredSamples'
import type { Project } from '@/stores/sequencerStore'
import type { BankId } from '@busswerks/shared'

const BANK_IDS: readonly BankId[] = ['A', 'B', 'C', 'D']
import { PadModeToggle } from './PadModeToggle'
import { SequencerMidiPanel } from './SequencerMidiPanel'
import { AudioPanel } from '@/components/werkshop/AudioPanel'
// useSyncSequencerProjects now lives at the root level inside
// PlaybackProvider so hydrate runs even before SequencerPage mounts
// — necessary because the editor route can show a "Loading project…"
// spinner while the sync hook catches up on a freshly-navigated id.
import { useAllSamples } from '@/hooks/useSampleUpload'
import { exportProjectToWav } from '@/lib/exportProject'
import { FaDownload as Download } from 'react-icons/fa'
// Liveblocks wrappers (`ProjectRoom`, `PresenceCanvasOverlay`) are
// intentionally NOT imported here. They're paused for the sequencer
// during the Projects refactor — single-user Liveblocks rooms were
// contributing to the duplicate-pattern pile-up and the new Projects
// structure will re-establish multiplayer on a firmer footing. For
// now the sequencer renders as a plain React tree; the presence
// feature remains active for Layouts (which is where it was proven
// first). See `useSyncSequencerProjects` for the matching pause on
// cloud sync.

const MIDI_VELOCITY = 100

export function SequencerPage() {
  // `user` isn't read directly; features come through the effective
  // hook (which merges DEV tier overrides on top of the server tier).
  useAuth()
  const { features } = useEffectiveFeatures()
  const platform = usePlatform()

  // Source-of-truth project is always derived from the store's map +
  // currentProjectId, so switching projects flows naturally through
  // the whole page without component-level project caching.
  const project = useSequencerStore(selectCurrentProject)
  const container = useSequencerStore(selectCurrentContainer)

  // Master FFT Analyzer seeding now lives in the store's `createEmpty
  // Container` + v13 persist migration (see MASTER_FFT_ANALYZER_EFFECT_ID).
  // The previous post-mount `useEffect` approach raced the container-
  // sync pull and could drop the seed before the Inspector rendered.
  // Keeping the deprecated `selectCurrentBuses` / `MASTER_BUS_ID`
  // imports out of this file shrinks the reason-to-re-run surface.
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const currentStep = useSequencerStore(s => s.currentStep)
  const focusedPad = useSequencerStore(s => s.focusedPad)
  const layoutMode = useSequencerStore(s => s.layoutMode)
  const trackerFocus = useSequencerStore(s => s.trackerFocus)
  const setTrackerFocus = useSequencerStore(s => s.setTrackerFocus)
  const setBpm = useSequencerStore(s => s.setBpm)
  const setKit = useSequencerStore(s => s.setKit)
  const setMidiEnabled = useSequencerStore(s => s.setMidiEnabled)
  const setMidiInEnabled = useSequencerStore(s => s.setMidiInEnabled)
  const setPlaying = useSequencerStore(s => s.setPlaying)
  const setCurrentStep = useSequencerStore(s => s.setCurrentStep)
  const setFocusedPad = useSequencerStore(s => s.setFocusedPad)
  const songMode = useSequencerStore(s => s.songMode)
  const song = useSequencerStore(s => s.song)
  const songCursor = useSequencerStore(s => s.songCursor)
  // Tracker-only discoverability banner for the Scrambler. Layout-
  // aware surface so we don't intrude on users who aren't in the
  // tracker view (the Scrambler panel is already visible next to
  // the FX rail in other layouts).
  const scramblerEnabled = useSequencerStore(s => s.scramblerParams.enabled)
  const selectProject = useSequencerStore(s => s.selectProject)
  const setSongCursor = useSequencerStore(s => s.setSongCursor)
  const setSongMode = useSequencerStore(s => s.setSongMode)
  // Phase 5 — project-level Rubber Band conform flag. Still honoured
  // by the audio engine for projects that previously enabled it;
  // the UI toggle was removed pending a UX rethink (Plan Phase 1)
  // so the behaviour stays dormant by default on new projects.
  // Per-output latency compensation (ms). Positive values fire
  // MIDI note-on earlier than the matching audio so external
  // hardware lines up with in-app synth.
  // Read effective output latency — honours project MIDI overrides
  // when the active project has opted into one; otherwise falls
  // back to the global `midiStore`.
  const outputLatencyMs = useEffectiveMidi().outputLatencyMs
  const padNoteBindings = useMidiStore(s => s.padNoteBindings)
  const bindPadNote = useMidiStore(s => s.bindPadNote)

  // Backend project sync now mounted at the root via PlaybackProvider.
  // Plan 2b: sample list — used to resolve customSamples[pad] →
  // playUrl for the audio engine on each trigger.
  const { data: samples = [] } = useAllSamples()
  const sampleById = useMemo(
    () => new Map(samples.map(s => [s.id, s])),
    [samples],
  )
  const [exportState, setExportState] = useState<
    'idle' | 'rendering' | 'error'
  >('idle')
  // Progress 0..1 + stage label for the WAV export UI. Cleared
  // when exportState returns to idle.
  const [exportProgress, setExportProgress] = useState<{
    progress: number
    stage: string
  } | null>(null)

  // Transient "sample not loaded" toast — fired when the audio engine
  // tries to play a pad whose sample buffer isn't in memory yet. Under
  // normal conditions the prewarm effect below loads all referenced
  // samples, so this toast should never appear. If it does, something
  // regressed (e.g. a sample was deleted on the server, or a stale
  // playUrl expired mid-session). One message per sampleId per 1.5s
  // window so a rapid-fire roll against a missing sample doesn't spam.
  const [sampleMissToast, setSampleMissToast] = useState<string | null>(null)
  useEffect(() => {
    const recent = new Map<string, number>()
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ sampleId: string }>).detail
      const id = detail?.sampleId ?? '?'
      const now = Date.now()
      const last = recent.get(id) ?? 0
      if (now - last < 1500) return
      recent.set(id, now)
      const meta = sampleById.get(id)
      const label = meta?.name ?? id
      setSampleMissToast(`Sample "${label}" isn't loaded yet — retrying…`)
      window.setTimeout(() => setSampleMissToast(null), 2800)
    }
    window.addEventListener('busswerks:sample-miss', handler)
    return () => window.removeEventListener('busswerks:sample-miss', handler)
  }, [sampleById])

  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [midiPanelOpen, setMidiPanelOpen] = useState(false)
  const [audioPanelOpen, setAudioPanelOpen] = useState(false)
  // When set, the next incoming MIDI note binds to this pad index
  // instead of triggering it. Cleared after the bind lands. Drives
  // the MIDI Learn flow in SequencerMidiPanel.
  const [midiLearnArmedPad, setMidiLearnArmedPad] = useState<number | null>(
    null,
  )

  // Tier capabilities — read through `useEffectiveFeatures` so the
  // DEV tier-override panel can merge a fake tier on top of the real
  // server features. `user` is only used for identity (id / email).
  const sequencerSave = features?.sequencerSave ?? false
  const unlockedAllKits = features?.sequencerKits === 'all'
  const maxProjects = features?.maxSequencerProjects ?? 1

  const { preloadSample } = useAudioEngine()
  // Shared trigger pipeline — resolvedPads / firePad / fireMidiPad
  // also back the root-level PlaybackProvider's scheduler, so this
  // page's pad-click / MIDI-in handlers and the scheduler tick both
  // go through the exact same code path.
  const {
    resolvedPads,
    firePad,
    fireMidiPad,
    attackPad,
    releasePad,
    resume,
    currentTime,
  } = useSequencerTriggers()

  // Per-pad audition pre-warm. Clicking a sample pad live without
  // prior playback needs its buffer decoded so the first hit plays
  // immediately. We only pre-warm the FOCUSED pad so page-load stays
  // cheap (full-project preload now happens lazily on Play — see
  // `handleTogglePlay` below). Additional pads warm on click via the
  // fallback fetch-then-play path in the engine.
  useEffect(() => {
    if (!project?.padDefs) return
    if (samples.length === 0) return
    const padDef = project.padDefs[focusedPad]
    if (!padDef) return
    let sampleId: string | undefined
    if (padDef.kind === 'sample' || padDef.kind === 'granular') {
      sampleId = padDef.sampleId
    } else if (padDef.kind === 'instrument' && padDef.sampleId) {
      sampleId = padDef.sampleId
    }
    if (!sampleId) return
    const meta = sampleById.get(sampleId)
    if (!meta?.playUrl) return
    void preloadSample(sampleId, meta.playUrl)
  }, [project?.padDefs, samples, sampleById, preloadSample, focusedPad])
  const {
    requestAccess,
    dispatchVirtualMidi,
    dispatchToHardwareOutput,
    subscribeSequencerNoteIn,
  } = useWebMidi()

  // Wake up Web MIDI on mount so the sequencer's output reaches any
  // hardware the user already wired up in /midi.
  useEffect(() => {
    requestAccess()
  }, [requestAccess])

  const kit = KITS[project.kitId]
  // `resolvedPads`, `firePad`, `fireMidiPad` now come from the
  // shared `useSequencerTriggers` hook above so the pad grid and the
  // root-level PlaybackProvider scheduler use the same code path.

  // Pad edit mode: when on, pad taps select + open the Inspector
  // for editing instead of firing the sound. Long-press always
  // behaves like edit-mode-tap, regardless of this flag.
  const padEditMode = useUIStore(s => s.padEditMode)
  const setInspectorOpen = useInspectorStore(s => s.setOpen)

  /**
   * Pointer click on a pad. Resumes the audio context on first user
   * gesture, fires the voice immediately, and focuses the pad in the
   * step grid.
   */
  const padMode = useUIStore(s => s.padMode)
  const fxRailCollapsed = useUIStore(s => s.fxRailCollapsed)
  const toggleFxRailCollapsed = useUIStore(s => s.toggleFxRailCollapsed)
  const queuePatternSwitch = useSequencerStore(s => s.queuePatternSwitch)

  const handlePadClick = useCallback(
    (padIndex: number) => {
      // Pattern mode → taps queue a pattern switch instead of firing
      // audio. Pad N maps to the Nth pattern in the active bank's
      // ordered list; slots beyond `patternIds.length` are no-ops.
      // The switch uses the existing `queuePatternSwitch` so it lands
      // on the next downbeat just like the top-bar pattern tabs.
      if (padMode === 'pattern') {
        const bank = container?.banks[container.activeBankId]
        const targetId = bank?.patternIds[padIndex]
        if (targetId && bank) {
          queuePatternSwitch(container.activeBankId, targetId)
        }
        return
      }

      const pad = resolvedPads[padIndex]
      // Empty slot → focus it so the Cell Editor's Pad tab pulls
      // it in (kind picker lets the user drop content inline).
      // Previously this fell through to the full PadEditor modal;
      // with the Pad tab shipping in #199 the inline path is the
      // primary editor. The modal stays reachable via ⚙ Advanced
      // for envelope / granular / custom MIDI pads until those
      // sections also migrate to the tab.
      if (!pad) {
        setFocusedPad(padIndex)
        return
      }
      // Edit mode short-circuits the audio path: tap-to-select
      // + open the Inspector for live parameter editing without
      // firing the sound. Matches the Slicer's edit toggle and
      // the rest of the Werkshop's "edit vs. play" split.
      if (padEditMode) {
        setFocusedPad(padIndex)
        setInspectorOpen(true)
        return
      }
      resume()
      const when = currentTime()
      firePad(padIndex, when, { focusOnClick: true })
    },
    [
      currentTime,
      firePad,
      resume,
      resolvedPads,
      padEditMode,
      padMode,
      container,
      queuePatternSwitch,
      setFocusedPad,
      setInspectorOpen,
    ],
  )

  // Long-press handler shared between every pad: same behaviour
  // as edit-mode-tap regardless of the current mode. Lets users
  // pop the editor open for a specific pad without leaving
  // performance mode.
  const handlePadLongPress = useCallback(
    (padIndex: number) => {
      setFocusedPad(padIndex)
      setInspectorOpen(true)
    },
    [setFocusedPad, setInspectorOpen],
  )

  /**
   * Incoming MIDI from a hardware input routed to the Sequencer
   * virtual destination. Resolution order:
   *
   *   1. MIDI Learn — if a pad is armed, bind the note instead of
   *      firing. One-shot: disarm after capture.
   *   2. Explicit `padNoteBindings` (set via MIDI Learn). Wins over
   *      the chromatic default — user-bound notes always take
   *      precedence.
   *   3. Chromatic-by-pad default: pad i is triggered by note
   *      `48 + i` (C3, C#3, D3, …, D#4 across pads 1..16) as long
   *      as the pad has content and no explicit binding. This is
   *      DELIBERATELY independent of `resolved.midiNote` — that
   *      field is the pad's MIDI-OUT note (what the pad EMITS when
   *      it fires), not what triggers it. Keeping the trigger note
   *      stable at 48+i means users can retune synth/midi pads
   *      freely without losing their controller mapping.
   *   4. Custom-MIDI overflow pads (matched by note + channel).
   *
   * Unmatched notes are ignored.
   */
  useEffect(() => {
    // Active-notes registry keyed by `${note}-${channel}`. Holds the
    // voice token returned from `attackPad` so the matching note-off
    // knows which voice to release. Non-instrument pads attack-fire
    // through `firePad` and return null; the entry still exists (with
    // token=null) so note-off can clean up the map entry but nothing
    // is released — the one-shot already played out.
    //
    // Kept in a ref inside the effect so React's render cycle can't
    // clear live voices on an unrelated re-mount.
    const heldNotes = new Map<string, number | null>()
    const heldKey = (note: number, channel: number) => `${note}-${channel}`

    const unsubscribe = subscribeSequencerNoteIn(ev => {
      // Master MIDI-in gate: stops any routed hardware input from
      // triggering pads / sequencer / fx. Learn mode is intentionally
      // allowed through so the user can still arm bindings with the
      // input temporarily disabled — flipping the toggle back on then
      // uses the learned binding immediately.
      if (midiLearnArmedPad === null && !(project.midiInEnabled ?? true)) {
        return
      }

      const key = heldKey(ev.note, ev.channel)

      if (!ev.isNoteOn) {
        // Note-off: release the held voice if one exists.
        const token = heldNotes.get(key)
        if (token !== undefined) {
          releasePad(token)
          heldNotes.delete(key)
        }
        return
      }

      // (1) Learn mode — capture + return.
      if (midiLearnArmedPad !== null) {
        bindPadNote(midiLearnArmedPad, ev.note)
        setMidiLearnArmedPad(null)
        return
      }

      resume()

      // (2) Explicit binding.
      const boundEntry = Object.entries(padNoteBindings).find(
        ([, bound]) => bound === ev.note,
      )
      if (boundEntry) {
        const idx = Number(boundEntry[0])
        if (resolvedPads[idx]) {
          const token = attackPad(idx, { velocity: ev.velocity })
          heldNotes.set(key, token)
        }
        return
      }

      // (3) Chromatic-by-pad default — pad i is triggered by note
      //     48+i regardless of what that pad emits on MIDI-out.
      //     Skip pads that have an explicit binding (so the override
      //     layer stays authoritative) or that are empty.
      const chromaticIdx = ev.note - 48
      if (
        chromaticIdx >= 0 &&
        chromaticIdx < resolvedPads.length &&
        resolvedPads[chromaticIdx] &&
        padNoteBindings[chromaticIdx] === undefined
      ) {
        const token = attackPad(chromaticIdx, { velocity: ev.velocity })
        heldNotes.set(key, token)
        return
      }

      // (4) Overflow MIDI pads — one-shot fire (MIDI pads have no
      //     hold semantics; they're pure MIDI-out routers).
      const customMatch = project.midiPads.find(
        mp => mp.note === ev.note && mp.channel === ev.channel,
      )
      if (customMatch) {
        fireMidiPad(customMatch, currentTime(), {
          velocity: ev.velocity,
          sendMidi: false,
        })
      }
    })
    return () => {
      // Clean up any held voices before detaching the listener so a
      // hot-reload doesn't leak voices.
      for (const token of heldNotes.values()) {
        if (token !== null) releasePad(token)
      }
      heldNotes.clear()
      unsubscribe()
    }
  }, [
    subscribeSequencerNoteIn,
    resolvedPads,
    attackPad,
    releasePad,
    fireMidiPad,
    project.midiPads,
    project.midiInEnabled,
    resume,
    currentTime,
    midiLearnArmedPad,
    bindPadNote,
    padNoteBindings,
  ])

  const handleMidiPadClick = useCallback(
    (padId: string) => {
      const mp = project.midiPads.find(m => m.id === padId)
      if (!mp) return
      resume()
      fireMidiPad(mp, currentTime())
    },
    [currentTime, fireMidiPad, project.midiPads, resume],
  )

  // "Preparing samples" UI state — rendered near the transport so
  // the user sees why Play isn't starting instantly on first press.
  // Ephemeral; cleared once the preloads resolve.
  const [isPreparing, setIsPreparing] = useState(false)

  const handleTogglePlay = useCallback(async () => {
    if (isPlaying) {
      setPlaying(false)
      return
    }
    resume()
    // In song mode, seed the active project to the first song entry
    // so the scheduler starts on the chosen project; setPlaying
    // already initializes the cursor.
    if (songMode && song.length > 0) {
      const first = song[0]
      if (first.projectId !== project.id) {
        selectProject(first.projectId)
      }
    }
    // Scenes (Ableton-session) mode: if the scheduler doesn't know
    // which scene to run, start at scene 0. `currentSceneIndex` is
    // runtime-only, so it's `null` on fresh load even when songMode
    // is already on from persisted state.
    if (songMode) {
      const containerNow =
        useSequencerStore.getState().containers[
          useSequencerStore.getState().currentContainerId
        ]
      if (
        containerNow &&
        containerNow.scenes.length > 0 &&
        useSequencerStore.getState().currentSceneIndex === null
      ) {
        useSequencerStore.getState().selectScene(0)
      }
    }
    // Preload every sample the upcoming playback will need before
    // the scheduler starts. Page-load stays cheap (no eager
    // download); users who never hit Play never pay the fetch cost.
    // First Play press pauses briefly while buffers decode — better
    // than stuttered first-bar playback with "sample isn't loaded
    // yet" toasts on every hit. `preloadSample` dedupes concurrent
    // requests, so repeated Play presses don't re-fetch.
    //
    // Scope:
    //   - Song mode → walk every pattern assigned to a scene slot
    //     across every bank. Ensures pattern switches within the
    //     scene cycle don't stall on cold samples.
    //   - Song mode without scenes but with a song entry list →
    //     walk every entry's project.
    //   - Single-pattern mode → just the active pattern.
    const storeSnapshot = useSequencerStore.getState()
    const projectsMap = storeSnapshot.projects
    const requiredProjects: Project[] = [project]
    if (songMode) {
      if (container && container.scenes.length > 0) {
        for (const scene of container.scenes) {
          for (const bankId of BANK_IDS) {
            const id = scene.cells[bankId]
            if (!id) continue
            const p = projectsMap[id]
            if (p) requiredProjects.push(p)
          }
        }
      }
      for (const entry of song) {
        const p = projectsMap[entry.projectId]
        if (p) requiredProjects.push(p)
      }
    }
    const required = collectRequiredSampleIds(requiredProjects, samples)
    if (required.length > 0) {
      setIsPreparing(true)
      try {
        await Promise.all(
          required.map(r => preloadSample(r.sampleId, r.playUrl)),
        )
      } finally {
        setIsPreparing(false)
      }
    }
    setPlaying(true)
  }, [
    isPlaying,
    preloadSample,
    project,
    resume,
    samples,
    selectProject,
    setPlaying,
    song,
    songMode,
  ])

  // Song-mode cursor advance + the scheduler tick both now live in
  // `providers/PlaybackProvider.tsx` so playback keeps running when
  // the user navigates away from this page.

  // Space bar play/stop matches DAW convention.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      e.preventDefault()
      handleTogglePlay()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleTogglePlay])

  // "I" toggles the Inspector dock. Skipped when the user is typing
  // in an input/textarea so the shortcut doesn't intercept normal
  // typing (ASR: matches the Space-bar gate above). Modifier-free
  // so it's a single-key pro gesture.
  const toggleInspector = useInspectorStore(s => s.toggle)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'i' && e.key !== 'I') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (target?.isContentEditable) return
      e.preventDefault()
      toggleInspector()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggleInspector])

  // Scheduler tick is owned by the root-level PlaybackProvider so
  // playback keeps running when the user navigates to /midi or
  // /slicer. See providers/PlaybackProvider.tsx.

  // Layout decision: Tracker is full-width because the inspector
  // uses the right rail. Other modes get the traditional
  // pads-left / steps-right split on large screens.
  const isTracker = layoutMode === 'tracker'

  // Defensive: if hydration momentarily lands with no projects in the
  // store (e.g. a bad `hydrateFromRemote` payload), `project` can be
  // undefined. Accessing `project.id` / `project.name` inside the
  // render tree then surfaces as a cryptic "Something went wrong"
  // error boundary. The store seeds DEFAULT_PROJECT_ID on init, so
  // this is a rare path — but a pass-through empty state beats
  // tripping the boundary.
  if (!project) {
    return (
      <div className="min-h-[100dvh] bg-surface-base flex items-center justify-center text-content-tertiary text-sm">
        Loading sequencer…
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] lg:h-[100dvh] bg-surface-base lg:flex lg:flex-col">
      {sampleMissToast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-[max(env(safe-area-inset-top),1rem)] left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-md bg-destructive/90 text-[color:var(--destructive-ink)] text-xs font-mono shadow-high border border-destructive"
        >
          ⚠ {sampleMissToast}
        </div>
      )}
      {isPreparing && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-[max(env(safe-area-inset-top),1rem)] left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-md bg-accent/90 text-[color:var(--accent-ink)] text-xs font-mono shadow-high border border-accent flex items-center gap-2"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--accent-ink)] animate-pulse" />
          Preparing samples…
        </div>
      )}
      <WorkspaceHeader
        title={
          container
            ? `${container.name} · ${container.banks[container.activeBankId]?.label ?? 'Bank A'} · ${project.name}`
            : project.name
        }
        workspaceSwitcher="werkshop"
        rightExtras={
          <>
            <ProjectSaveRename />
            <InspectorToggle />
          </>
        }
      />

      <main
        className={
          // Full-width on every runtime. The old web path capped at
          // max-w-7xl (1280 px) which left huge pillarbox padding on
          // ultra-wide monitors while squeezing the step grid + FX
          // rail unnecessarily. Catalyst already used w-full; web now
          // matches — the three-column editor expands to fill the
          // viewport's horizontal real estate on any display.
          //
          // `lg:flex-1 lg:min-h-0 lg:overflow-hidden` locks the main
          // to the viewport height at lg+ so the 3-column editor grid
          // below can hand per-column overflow to the left pad
          // column and keep the step grid + FX rail visually pinned.
          (platform.isNative
            ? 'w-full px-3 sm:px-4 py-4 flex flex-col gap-3'
            : 'w-full px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-4') +
          ' lg:flex-1 lg:min-h-0 lg:overflow-hidden'
        }
      >
        {/* Transport row.
            Layout: the fixed-size Transport block on the left, a
            right-hand column that stacks TWO sub-rows:
              1. Panel + persistence affordances (MIDI, AUDIO, router
                 badge, Auto-saved, Export WAV)
              2. Kit picker + Layout switcher
            Both sub-rows sit flush with the top / bottom edges of
            Transport so the pane reads as one cohesive unit even on
            wide displays. Before this layout the right-side controls
            wrapped awkwardly at Catalyst's landscape width. */}
        <div className="flex items-stretch gap-3 flex-wrap">
          <Transport
            isPlaying={isPlaying}
            bpm={project.bpm}
            midiEnabled={project.midiEnabled}
            midiInEnabled={project.midiInEnabled ?? true}
            onTogglePlay={handleTogglePlay}
            onChangeBpm={setBpm}
            onToggleMidi={() => setMidiEnabled(!project.midiEnabled)}
            onToggleMidiIn={() =>
              setMidiInEnabled(!(project.midiInEnabled ?? true))
            }
          />
          <div className="flex flex-1 flex-col justify-between gap-2 min-w-0">
            {/* Sub-row 1 — panels + persistence cluster on the
                right, matching the vertical top edge of the
                transport pane. */}
            <div
              className={`flex items-center gap-2 min-w-0 ${
                platform.isNative ? 'flex-wrap' : 'flex-wrap'
              }`}
            >
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setMidiPanelOpen(open => !open)}
                aria-pressed={midiPanelOpen}
                title="Open the MIDI router + controller mapping inline"
                className={`rounded-md border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                  midiPanelOpen
                    ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                    : 'border-brd-subtle text-content-secondary hover:border-brd-emphasis'
                }`}
              >
                MIDI
              </button>
              <button
                type="button"
                onClick={() => setAudioPanelOpen(open => !open)}
                aria-pressed={audioPanelOpen}
                title="Audio I/O — output device, buses, input monitoring"
                className={`rounded-md border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                  audioPanelOpen
                    ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                    : 'border-brd-subtle text-content-secondary hover:border-brd-emphasis'
                }`}
              >
                AUDIO
              </button>
              <VirtualInputBadge />
              {/* Save cluster: auto-saved pill sits next to the WAV
                  export button so both persistence affordances are
                  grouped at the end of the row. */}
              <button
                type="button"
                onClick={() => {
                  if (!sequencerSave) {
                    setUpgradeOpen(true)
                  }
                }}
                disabled={sequencerSave}
                className={
                  sequencerSave
                    ? 'rounded-md border border-brd-subtle px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-content-tertiary cursor-default'
                    : 'rounded-md border border-brd-subtle px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-content-secondary hover:text-content-primary hover:border-brd-emphasis'
                }
                title={
                  sequencerSave
                    ? 'Project syncs to your account across devices.'
                    : 'Upgrade to Pro to save projects across sessions.'
                }
              >
                {sequencerSave ? 'Auto-saved' : '🔒 Save'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (exportState === 'rendering') return
                  setExportState('rendering')
                  setExportProgress({ progress: 0, stage: 'Preparing' })
                  try {
                    await exportProjectToWav(project, samples, p =>
                      setExportProgress(p),
                    )
                    setExportState('idle')
                    setExportProgress(null)
                  } catch (err) {
                    console.error('WAV export failed:', err)
                    setExportState('error')
                    setExportProgress(null)
                    setTimeout(() => setExportState('idle'), 3000)
                  }
                }}
                disabled={exportState === 'rendering'}
                className="inline-flex items-center gap-1 rounded-md border border-brd-subtle px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-content-secondary hover:text-content-primary hover:border-brd-emphasis disabled:opacity-60"
                title="Render the current pattern to a downloadable WAV"
              >
                <Download className="w-3 h-3" />
                {exportState === 'rendering'
                  ? exportProgress
                    ? `${Math.round(exportProgress.progress * 100)}% · ${exportProgress.stage}`
                    : 'Rendering…'
                  : exportState === 'error'
                    ? 'Export failed'
                    : 'Export WAV'}
              </button>
            </div>
            {/* Sub-row 2 — kit + layout selectors sit flush with the
                bottom edge of Transport so both rows line up with
                the transport block's top and bottom. */}
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <KitPicker
                value={project.kitId}
                unlockedAll={unlockedAllKits}
                onChange={setKit}
                onLockedAttempt={() => setUpgradeOpen(true)}
              />
              <div className="flex-1" />
            </div>
          </div>
        </div>

        {midiPanelOpen && (
          <SequencerMidiPanel
            resolvedPads={resolvedPads}
            project={project}
            midiLearnArmedPad={midiLearnArmedPad}
            onArmPad={setMidiLearnArmedPad}
            onUpgradeNeeded={() => setUpgradeOpen(true)}
            onClose={() => {
              setMidiPanelOpen(false)
              setMidiLearnArmedPad(null)
            }}
          />
        )}

        {audioPanelOpen && (
          <AudioPanel onClose={() => setAudioPanelOpen(false)} />
        )}

        {/* Main editor region — 3 columns on lg+.
            Pad column   : Bank switcher + Configure pads → PadGrid → StepInspector (always visible)
            Step column  : PatternBar → StepGrid
            FX rail      : FxBank

            At lg+ the grid claims the remaining viewport height and
            each column gets its own scrollbar. The pad column is the
            one that scrolls when its content exceeds the viewport
            (cell inspector open with rich extras); step grid + FX
            rail visually stay in place. */}
        {/* Three-column editor grid:
              - Pads column: 0.7fr — narrower than it used to be so the
                step grid gets the lion's share of horizontal real
                estate (user felt pads dominated on Catalyst)
              - Step grid: 1.6fr — primary performance surface
              - FX rail: minmax(240px, 290px) — slight bump over the
                prior 220/260 pair so Scrambler panel labels + meter
                values read cleanly without truncation */}
        <div
          className={`relative grid grid-cols-1 gap-3 lg:flex-1 lg:min-h-0 ${
            fxRailCollapsed
              ? 'lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.6fr)_2rem]'
              : 'lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.6fr)_minmax(240px,290px)]'
          }`}
        >
          {/* ── PAD COLUMN ────────────────────────────────────
              Header strip (Banks + Keys on the left, ⚙ Pads on the
              right) lives at the same vertical position as the
              Step and FX headers. All three share
              `h-9 shrink-0 items-center` + no-wrap so the tallest
              child (the BankSwitcher pill bar) doesn't expand the
              pad row past the others — their card borders beneath
              line up without per-column padding math.

              Sample upload moved out of this header — it's already
              available from PadEditor's sample picker + the main
              sample library, and having a third entry point here
              crowded the pad column on Catalyst without adding
              capability. */}
          <div className="flex flex-col gap-2 min-w-0 lg:h-full lg:min-h-0 lg:overflow-y-auto">
            <div className="flex items-center gap-2 h-9 shrink-0 min-w-0">
              <BankSwitcher />
              <PadModeToggle />
              <div className="flex-1" />
              <PadEditModeToggle />
            </div>
            {/* Focused-cell exit affordance. Sits on its own row
                below the header so the header stays clean and the
                Keys / Sample / Pads group aligns to a single line. */}
            {!isTracker && trackerFocus && (
              <button
                type="button"
                onClick={() => setTrackerFocus(null)}
                title="Expand pads (clears the focused cell in the Cell Editor)"
                className="self-start px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent transition-colors"
              >
                ▾ Expand pads
              </button>
            )}
            {/* Collapse the pad grid while a cell is focused so the
                Cell Editor can claim that vertical space. Tracker
                mode replaces the pad column entirely so the
                collapse only fires in 2×8 / 8×8 / 4×4. */}
            {(isTracker || !trackerFocus) && (
              <PadGrid
                resolvedPads={resolvedPads}
                focusedPad={focusedPad}
                onPadTrigger={handlePadClick}
                onPadLongPress={handlePadLongPress}
              />
            )}
            {/* Per-pad sample deletion was previously offered here
                as a small "✕ sample · pad N" link. Users kept
                triggering it by accident when they meant to focus
                the pad; pad editing / removal now lives exclusively
                in the Pad Editor dialog where it's a deliberate
                gesture with confirmation. */}
            {/* Always-visible per-step inspector. Replaces the old
                long-press overlay so users can see the selected
                cell's velocity / pitch / FX / envelope / roll at a
                glance without having to popover. When no cell is
                focused, the panel renders a short hint instead of
                hiding, so its height stays stable. */}
            <StepInspectorPanel />
          </div>

          {/* ── STEP COLUMN ─────────────────────────────────
              Header strip hosts the Song toggle alongside the
              Patterns browser + pattern tabs. Same `h-9 shrink-0`
              contract as the Pad and FX headers so all three
              columns' cards start at the same Y. */}
          <div className="flex flex-col gap-2 min-w-0 lg:h-full lg:min-h-0 lg:overflow-hidden">
            <div className="flex items-center gap-2 h-9 shrink-0 min-w-0 overflow-x-auto">
              <button
                type="button"
                onClick={() => setSongMode(!songMode)}
                aria-pressed={songMode}
                title="Show the Scenes × Banks arrangement above the step editor"
                className={`px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border transition-colors ${
                  songMode
                    ? 'bg-second text-[color:var(--second-ink,white)] border-second'
                    : 'text-content-tertiary border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
                }`}
              >
                Song
              </button>
              <ProjectBar
                maxProjects={maxProjects}
                onLimitReached={() => setUpgradeOpen(true)}
              />
            </div>
            {/* Song mode: Ableton-session grid sits ABOVE the step
                editor. The song pane is content-sized — it takes
                only the height it needs — but caps at 50% of the
                step column so an arrangement with many scenes
                can't crowd out the step editor. The step grid
                claims every remaining pixel, and scrolls
                internally when its content overflows (e.g. 128
                steps at 8×8). Outside song mode the step grid
                gets the full column height. */}
            {songMode && (
              <div className="shrink-0 lg:max-h-[50%] overflow-hidden flex flex-col min-h-0">
                <SongLayout />
              </div>
            )}
            {isTracker && scramblerEnabled && (
              // Discoverability nudge: the Tracker replaces the Pad
              // Grid visually, so a user new to the session might
              // not notice the Scrambler still sitting in the FX
              // column actively mangling their hits. This banner
              // points them at it only when the Scrambler is on —
              // zero intrusion when it's not.
              <div className="shrink-0 rounded-md border border-second/40 bg-[color:var(--second-soft)] px-3 py-1.5 flex items-center gap-2 text-[11px]">
                <span aria-hidden>🎲</span>
                <span className="font-mono text-second">Scrambler on</span>
                <span className="text-content-tertiary">
                  — hits are being randomized at fire time. Tune or disable from
                  the Scrambler panel under the FX rail.
                </span>
              </div>
            )}
            <div className="flex flex-col lg:flex-1 min-h-0 overflow-y-auto">
              <SequencerTabs
                gridView={
                  <StepGrid
                    onEditPad={padIndex => {
                      // Redirect the Tracker's "+ Add channel"
                      // button + any other edit-pad callers to
                      // focus the pad. The Cell Editor auto-
                      // selects its Pad tab when only a pad is
                      // focused (no cell), so the user lands
                      // directly on the per-pad editor.
                      if (padIndex !== null) setFocusedPad(padIndex)
                    }}
                  />
                }
              />
            </div>
          </div>

          {/* ── FX COLUMN ───────────────────────────────────
              Latch + Hold pills sit as siblings above the FxBank
              rail so the rail card's top border aligns with the
              Pads and Song/Step cards (same `h-9 shrink-0`
              contract). Left-aligned to match the other columns'
              leading-edge controls. */}
          {fxRailCollapsed ? (
            // Collapsed: just the expand button on a narrow strip
            // so the main pane gets the FX rail's 240-290px back.
            // Persistent layout pref — reload lands back collapsed.
            <div className="flex flex-col items-center gap-1 h-9 lg:h-full lg:min-h-0 pt-0">
              <button
                type="button"
                onClick={toggleFxRailCollapsed}
                title="Expand FX rail"
                aria-label="Expand performance FX rail"
                className="w-6 h-9 flex items-center justify-center rounded-md border border-brd-subtle bg-surface-sunken text-content-tertiary hover:text-accent hover:border-[color:var(--accent-halo)] transition-colors"
              >
                <span className="text-[10px] font-mono leading-none">◀</span>
              </button>
              <span
                aria-hidden="true"
                className="hidden lg:block text-[9px] font-mono uppercase tracking-wider text-content-tertiary/60 rotate-180 [writing-mode:vertical-rl] mt-2"
              >
                FX Rail
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2 min-w-0 lg:h-full lg:min-h-0 lg:overflow-y-auto">
              <div className="flex items-center gap-2 h-9 shrink-0 min-w-0">
                <LatchPill />
                <HoldPill />
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={toggleFxRailCollapsed}
                  title="Collapse FX rail"
                  aria-label="Collapse performance FX rail"
                  className="w-6 h-7 flex items-center justify-center rounded-md border border-brd-subtle bg-surface-sunken text-content-tertiary hover:text-accent hover:border-[color:var(--accent-halo)] transition-colors"
                >
                  <span className="text-[10px] font-mono leading-none">▶</span>
                </button>
              </div>
              {/* Scrambler panel sits above the FX rail — collapsed
                  by default takes ~24 px, expanded parks the sliders
                  here so the 6-button FX rail below never gets
                  squeezed off the viewport on iPad / Catalyst. Help
                  text lives in hover tooltips rather than inline
                  paragraphs to keep the panel narrow. */}
              <ScramblerPanel />
              <div className="flex-1 min-h-0">
                <FxBank variant="rail" />
              </div>
            </div>
          )}
        </div>

        <p className="text-[11px] font-mono text-content-tertiary text-center">
          Click a pad to audition and focus its row. Space plays / stops. Wire a
          hardware input to <span className="text-second">Sequencer</span> in
          the MIDI Router to trigger pads externally.
        </p>
      </main>

      <UpgradeDialog
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />
      <Inspector />
    </div>
  )
}

/**
 * The FX rail's Latch toggle, rendered as a sibling above the rail
 * card so its top border aligns with the Banks and Patterns header
 * strips over the other two columns. Backed by the store's
 * `fxLatchMode` slice so FxBank and this pill stay in sync.
 * Mutually exclusive with Hold — enabling one disables the other
 * via the setter (see `setFxLatchMode` / `setFxHoldMode`).
 */
function LatchPill() {
  const latchMode = useSequencerStore(s => s.fxLatchMode)
  const setLatchMode = useSequencerStore(s => s.setFxLatchMode)
  return (
    <button
      type="button"
      aria-pressed={latchMode}
      onClick={() => setLatchMode(!latchMode)}
      title={
        latchMode
          ? 'LATCH on — taps toggle FX engage state. Tap LATCH to return to momentary.'
          : 'LATCH off — FX engage while pressed. Tap LATCH to keep them on.'
      }
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border transition-colors ${
        latchMode
          ? 'bg-second text-[color:var(--second-ink,white)] border-second'
          : 'text-content-tertiary border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
      }`}
    >
      Latch
    </button>
  )
}

/**
 * Hold — Koala-style performance mode. Tapping an FX pad when Hold
 * is engaged turns the FX on and sets its amount from the touch Y;
 * it never toggles off. Re-tapping an already-engaged pad "re-faders"
 * its amount rather than releasing it. Mutually exclusive with
 * Latch (enabling one disables the other).
 */
function HoldPill() {
  const holdMode = useSequencerStore(s => s.fxHoldMode)
  const setHoldMode = useSequencerStore(s => s.setFxHoldMode)
  return (
    <button
      type="button"
      aria-pressed={holdMode}
      onClick={() => setHoldMode(!holdMode)}
      title={
        holdMode
          ? 'HOLD on — taps engage and re-fader FX but never toggle off. Tap HOLD to return to momentary.'
          : 'HOLD off — tap HOLD to lock FX on once touched, with drag still setting the amount.'
      }
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border transition-colors ${
        holdMode
          ? 'bg-second text-[color:var(--second-ink,white)] border-second'
          : 'text-content-tertiary border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
      }`}
    >
      Hold
    </button>
  )
}

/**
 * Pad edit-mode toggle — mirrors the Slicer's "Edit slices" switch.
 * When off (default), tapping a pad fires its sound. When on, tapping
 * a pad selects + opens the Inspector for editing without firing.
 * Long-press on any pad opens the Inspector regardless of this flag.
 */
function PadEditModeToggle() {
  const padEditMode = useUIStore(s => s.padEditMode)
  const toggle = useUIStore(s => s.togglePadEditMode)
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={padEditMode}
      title={
        padEditMode
          ? 'Edit ON — tapping a pad selects it for editing. Tap Edit to return to performance mode.'
          : 'Edit OFF — tapping a pad fires its sound. Tap Edit to switch to editing, or long-press any pad.'
      }
      className={`px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border transition-colors ${
        padEditMode
          ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
          : 'text-content-tertiary border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
      }`}
    >
      {padEditMode ? '✎ Edit' : 'Edit'}
    </button>
  )
}
